import React from 'react';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import EditCheckIn from '../Crud/EditCheckIn';
import { handleCheckoutAction, handleCheckinAction } from '../Utils/ReservationActionHelper';
import { formatToDisplayTime } from "../../../utils/dateTimeUtils";

export const getDetailedCheckOutColumns = (handleReservation, onDelClick, userRole) => [
    {
        field: "assetTag",
        headerName: "Asset Tag",
        width: 100,
    },
    {
        field: "assetItem",
        headerName: "Item",
        width: 150,
        renderCell: (params) => {
            const assetItem = params.row.assetItem;
            const assetDescription = params.row.assetDescription;
            
            if (assetDescription) {
                return `${assetItem} - (${assetDescription})`;
            } else {
                return assetItem || '';
            }
        }
    },
    {
        field: "kitItems",
        headerName: "Kit Items",
        width: 170,
        renderCell: (params) => {
            const kitItems = params.row.kitItems || [];
            if (kitItems.length === 0) {
                return <span style={{ color: '#666', fontSize: '0.8rem' }}>No items</span>;
            }
            
            return (
                <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '4px',
                    maxHeight: '80px',
                    overflowY: 'auto',
                    padding: '4px'
                }}>
                    {kitItems.map((item, index) => (
                        <Chip 
                            key={index}
                            label={
                                item.manufacturer && item.model ? 
                                    `${item.manufacturer} ${item.model}` : 
                                    item.description || 'Unknown Item'
                            }
                            size="small"
                            variant="outlined"
                            style={{ fontSize: '0.7rem' }}
                        />
                    ))}
                </div>
            );
        }
    },
    {
        field: "checkOutDateTime",
        headerName: "Check-Out Date & Time",
        width: 170,
        renderCell: (params) => {
            const date = params.row.checkOutDate;
            const time = formatToDisplayTime(params.row.checkOutTime);
            return `${time} | ${date}`;
        }
    },
    {
        field: "checkInDateTime",
        headerName: "Check-In Date & Time",
        width: 170,
        renderCell: (params) => {
            const date = params.row.checkInDate;
            const time = formatToDisplayTime(params.row.checkInTime);
            return `${time} | ${date}`;
        }
    },
    {
        field: "status",
        headerName: "Status",
        width: 90,
        renderCell: (params) => {
            const status = params.row.status;
            const getStatusText = (status) => {
                switch (status) {
                    case 'PENDING': return 'Pending';
                    case 'CHECKED_OUT': return 'Checked Out';
                    case 'CHECKED_IN': return 'Checked In';
                    case 'CANCELLED': return 'Cancelled';
                    default: return 'Unknown';
                }
            };
            return getStatusText(status);
        }
    },
    {
        field: "checkOut",
        headerName: "Check-Out",
        width: 140,
        renderCell: (params) => {
            if (userRole !== "ROLE_STUDENT") {
                const isCheckedOut = params.row.status === 'CHECKED_OUT';
                return (
                    <Button 
                        variant="contained" 
                        color="primary" 
                        size="small"
                        style={{ fontSize: '0.8rem' }} 
                        disabled={isCheckedOut}
                        onClick={(event) => {
                            event.stopPropagation(); // Prevent row click event
                            handleCheckoutAction(params.row, handleReservation);
                        }}
                    >
                        {isCheckedOut ? 'Already Checked Out' : 'Check-Out'}
                    </Button>
                );
            }
        }
    },
    {
        field: "cancelReservation",
        headerName: "",
        width: 100,
        renderCell: (params) => (
            <Button 
                variant="contained" 
                color="error" 
                size="small"
                style={{ fontSize: '0.8rem' }} 
                onClick={(event) => {
                    event.stopPropagation(); // Prevent row click event
                    onDelClick(event, params.row.reservationId);
                }}
            >
                Cancel
            </Button>
        )
    }
];

// Keep cancel functionality even if un implemented, if it need to be added in the future
export const getDetailedCheckInColumns = (handleReservation, onDelClick, userRole, fetchReservationsData, updateReservation) => [
    {
        field: "assetTag",
        headerName: "Asset Tag",
        width: 90,
    },
    {
        field: "assetItem",
        headerName: "Item",
        width: 120,
        renderCell: (params) => {
            const assetItem = params.row.assetItem;
            const assetDescription = params.row.assetDescription;
            
            if (assetDescription) {
                return `${assetItem} - (${assetDescription})`;
            } else {
                return assetItem || '';
            }
        }
    },
    {
        field: "assetDescription",
        headerName: "Kit Items",
        width: 170,
        renderCell: (params) => {
            const kitItems = params.row.kitItems || [];
            if (kitItems.length === 0) {
                return <span style={{ color: '#666', fontSize: '0.8rem' }}>No items</span>;
            }
            
            return (
                <div style={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: '4px',
                    maxHeight: '80px',
                    overflowY: 'auto',
                    padding: '4px'
                }}>
                    {kitItems.map((item, index) => (
                        <Chip 
                            key={index}
                            label={
                                item.manufacturer && item.model ? 
                                    `${item.manufacturer} ${item.model}` : 
                                    item.description || 'Unknown Item'
                            }
                            size="small"
                            variant="outlined"
                            style={{ fontSize: '0.7rem' }}
                        />
                    ))}
                </div>
            );
        }
    },
    {
        field: "checkOutDateTime",
        headerName: "Check-Out Date & Time",
        width: 170,
        renderCell: (params) => {
            const date = params.row.checkOutDate;
            const time = formatToDisplayTime(params.row.checkOutTime);
            return `${time} | ${date}`;
        }
    },
    {
        field: "checkInDateTime",
        headerName: "Check-In Date & Time",
        width: 170,
        renderCell: (params) => {
            const date = params.row.checkInDate;
            const time = formatToDisplayTime(params.row.checkInTime);
            return `${time} | ${date}`;
        }
    },
    {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => {
            const status = params.row.status;
            const getStatusText = (status) => {
                switch (status) {
                    case 'PENDING': return 'Pending';
                    case 'CHECKED_OUT': return 'Checked Out';
                    case 'CHECKED_IN': return 'Checked In';
                    case 'CANCELLED': return 'Cancelled';
                    default: return 'Unknown';
                }
            };
            return getStatusText(status);
        }
    },
    {
        field: "checkIn",
        headerName: "Check-In",
        width: 150,
        renderCell: (params) => {
            if (userRole !== "ROLE_STUDENT") {
                const isCheckedIn = params.row.status === 'CHECKED_IN';
                return (
                    <Button 
                        variant="contained" 
                        color="secondary" 
                        size="small"
                        style={{ fontSize: '0.8rem' }} 
                        disabled={isCheckedIn}
                        onClick={(event) => {
                            event.stopPropagation(); // Prevent row click event
                            handleCheckinAction(params.row, handleReservation);
                        }}
                    >
                        {isCheckedIn ? 'Already Checked In' : 'Check-In'}
                    </Button>
                );
            }
        }
    },
    {
        field: "actions",
        headerName: "",
        renderCell: (params) => (
            userRole !== "ROLE_STUDENT" && (
                <EditCheckIn
                    data={params.row}
                    updateCheckIn={async (data, id) => {
                        const success = await updateReservation(data, id);
                        if (success) {
                            await fetchReservationsData();
                        } else {
                            alert('Something went wrong!');
                        }
                    }}
                />
            )
        )
    }
];
